import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type StepId = 'intake' | 'summary' | 'overview' | 'matching' | 'plan';

type JourneyStep = {
  id: StepId;
  label: string;
  description?: string;
  duration: string;
};

const steps: JourneyStep[] = [
  { id: 'intake',   label: 'Your Story',     description: 'Tell us about your situation',  duration: '5-10 min' },
  { id: 'summary',  label: 'Your Journey',   description: 'Understanding what to expect',   duration: '3-5 min' },
  { id: 'overview', label: 'Provider Types', description: 'Learn about care options',       duration: '5-7 min' },
  { id: 'matching', label: 'Find Providers', description: 'Match with specialists',         duration: '10-15 min' },
  { id: 'plan',     label: 'Care Plan',      description: 'Your personalized roadmap',      duration: '5-10 min' },
];

type JourneyProgressProps = {
  currentStep: StepId;
  sessionId?: string;
  carePlanId?: string | null;
};

export function JourneyProgress({ currentStep, sessionId, carePlanId }: JourneyProgressProps) {
  const navigate = useNavigate();
  const currentIndex = steps.findIndex((s) => s.id === currentStep);

  const trackInset = 100 / (steps.length * 2);
  const progressWidth =
    steps.length > 1 ? (currentIndex / (steps.length - 1)) * (100 - trackInset * 2) : 0;

  const goToStep = (stepId: StepId) => {
    switch (stepId) {
      case 'intake':
        navigate('/redesign-chatbot', { state: { sessionId } });
        break;
      case 'summary':
        navigate('/redesign-chatbot/situation-summary', { state: { sessionId } });
        break;
      case 'overview':
        navigate('/redesign-chatbot/provider-overview', { state: { sessionId, carePlanId } });
        break;
      case 'matching':
        navigate('/redesign-chatbot/matching/0', { state: { sessionId } });
        break;
      case 'plan':
        navigate('/redesign-chatbot/plan', { state: { sessionId } });
        break;
    }
  };

  return (
    <div className="sticky top-[var(--app-topbar-h,4rem)] z-40 border-b border-gray-200 bg-white px-2 py-3 shadow-sm sm:px-4 sm:py-4">
      <div className="mx-auto max-w-5xl">
        <div className="group relative flex items-start justify-between">
          {/* Background track */}
          <div
            className="absolute top-3 h-0.5 rounded-full bg-gray-200 sm:top-3.5"
            style={{ left: `${trackInset}%`, right: `${trackInset}%` }}
          />
          {/* Progress fill */}
          <div
            className="absolute top-3 h-0.5 rounded-full bg-[#026857] transition-[width] duration-700 ease-out sm:top-3.5"
            style={{ left: `${trackInset}%`, width: `${progressWidth}%` }}
          />

          {steps.map((step, index) => {
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            const isNavigable = index <= currentIndex;

            return (
              <div
                key={step.id}
                className="group/step relative z-[2] flex min-w-0 flex-1 flex-col items-center gap-1 px-0.5 sm:gap-2 sm:px-1"
              >
                <button
                  type="button"
                  disabled={!isNavigable}
                  onClick={() => isNavigable && goToStep(step.id)}
                  className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] sm:h-7 sm:w-7 sm:text-sm ${
                    isCompleted
                      ? 'cursor-pointer bg-[#026857] text-white group-hover/step:scale-125 group-hover/step:shadow-[0_4px_14px_rgba(2,104,87,0.25)]'
                      : isCurrent
                        ? 'cursor-default bg-[#026857] text-white ring-4 ring-[#026857]/20'
                        : 'cursor-not-allowed bg-gray-200 text-gray-500'
                  }`}
                >
                  {isCompleted ? <Check size={14} /> : <span>{index + 1}</span>}
                </button>

                <div className="w-full text-center">
                  <button
                    type="button"
                    disabled={!isNavigable}
                    onClick={() => isNavigable && goToStep(step.id)}
                    className={`w-full break-words text-[9px] font-medium leading-tight transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] sm:text-sm sm:font-semibold ${
                      isCurrent
                        ? 'cursor-default text-[#026857]'
                        : isCompleted
                          ? 'cursor-pointer text-gray-900 group-hover/step:scale-110 hover:text-[#026857]'
                          : 'cursor-not-allowed text-gray-500'
                    }`}
                  >
                    {step.label}
                  </button>
                  <div className="hidden max-h-0 overflow-hidden opacity-0 transition-all duration-300 group-hover:max-h-10 group-hover:opacity-80 group-hover/step:opacity-100 sm:block">
                    <div className="mx-auto mt-1 max-w-[100px] text-xs text-gray-500 transition-colors duration-300 group-hover/step:text-gray-700">
                      {step.description}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
